import { type AppDefaultInfo, type GlobalInfo } from "helpers/AppProvider.types";

import { type BTLoginTypes } from "types/enum";

/**
 * Synchronous snapshot of the values that non-React call sites used to read off
 * `window.btJScriptGlobals`.
 *
 * `GlobalInfo` is async and context-scoped, but some consumers can't reach context: module-eval
 * code, class field initializers, and plain `utilities/` functions. Populated by
 * `useGetGlobalInfoQuery` for both the API and btJScriptGlobals handlers.
 *
 * Readers must tolerate an empty snapshot — anything running before `AppProvider` resolves still
 * falls back to the global.
 */
interface IGlobalInfoSnapshot {
    loginType: BTLoginTypes | null;
    builderId: number | null;
    appDefaultInfo: AppDefaultInfo | null;
}

const emptySnapshot: IGlobalInfoSnapshot = {
    loginType: null,
    builderId: null,
    appDefaultInfo: null,
};

let snapshot: IGlobalInfoSnapshot = emptySnapshot;

/**
 * `AppProvider` defaults to the btJScriptGlobals handler, so a nested provider can mount on a page
 * that already loaded real global info. The API payload wins: once published, the inlined values
 * never replace it.
 */
type SnapshotSource = "api" | "btJScriptGlobals";

let snapshotSource: SnapshotSource | null = null;

/**
 * Called once per global info fetch, including `reloadGlobalInfo` refetches, so the snapshot
 * tracks the active session rather than the first one loaded.
 */
export function setGlobalInfoSnapshot(
    globalInfo: GlobalInfo,
    source: SnapshotSource = "api",
): void {
    if (source === "btJScriptGlobals" && snapshotSource === "api") {
        return;
    }

    snapshotSource = source;
    snapshot = {
        loginType: globalInfo.userInfo.loginType,
        builderId: globalInfo.builderInfo?.builderId ?? null,
        appDefaultInfo: globalInfo.appDefaultInfo,
    };
}

/**
 * Test-only: seeds the baseline snapshot between tests. The seed is not a real publish, so the
 * source latch is cleared too — otherwise the first seed would latch `"api"` for the whole file and
 * a test exercising the btJScriptGlobals path would silently hit the blocking branch.
 */
export function resetGlobalInfoSnapshotForTests(globalInfo: GlobalInfo): void {
    setGlobalInfoSnapshot(globalInfo);
    snapshotSource = null;
}

/**
 * Test-only: overrides individual snapshot fields without a full `GlobalInfo`. Production code
 * should never call this — the snapshot is owned by the global info query.
 */
export function overrideGlobalInfoSnapshotForTests(overrides: Partial<IGlobalInfoSnapshot>): void {
    snapshot = { ...snapshot, ...overrides };
}

/**
 * The user's portal/login type, or null before global info has resolved.
 *
 * @deprecated Use `UserInfoContext` wherever context is reachable. These readers exist only for
 * call sites that genuinely can't, and each is expected to go away under 282446.
 */
export function getSnapshotLoginType(): BTLoginTypes | null {
    return snapshot.loginType;
}

/**
 * The builder in session, or null when global info hasn't resolved or no builder is in session.
 *
 * @deprecated Use `BuilderInfoContext` wherever context is reachable — see
 * {@link getSnapshotLoginType} for why this reader still exists.
 */
export function getSnapshotBuilderId(): number | null {
    return snapshot.builderId;
}

/**
 * App default flags from the global info payload, or null before it resolves.
 *
 * @deprecated Use `AppDefaultInfoContext` wherever context is reachable — see
 * {@link getSnapshotLoginType} for why these readers still exist.
 */
export function getSnapshotAppDefaultInfo(): AppDefaultInfo | null {
    return snapshot.appDefaultInfo;
}
